import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { GripVertical, Plus, Trash2, Upload, Save, Eye, EyeOff, Star, Anchor, Pencil, X } from "lucide-react";
import { tr } from "../contexts/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u && u.startsWith("/uploads/") ? `${BACKEND}/api${u}` : u);
const inputCls = "border border-slate-300 focus:border-navy outline-none px-3 py-2 bg-white text-sm w-full";
const btnCls = "inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red transition-colors disabled:opacity-50";

export const BOAT_STATUSES = [
  { value: "available", label: "Disponible", cls: "bg-green-100 text-green-700" },
  { value: "in_use", label: "En utilisation", cls: "bg-blue-100 text-blue-700" },
  { value: "maintenance", label: "En entretien", cls: "bg-club-yellow/30 text-navy" },
  { value: "out_of_service", label: "Hors service", cls: "bg-red-100 text-red-700" },
  { value: "reserved", label: "Réservé", cls: "bg-purple-100 text-purple-700" },
];
const statusOf = (v) => BOAT_STATUSES.find((s) => s.value === v) || BOAT_STATUSES[0];

function useDragList(items, onReorder) {
  const dragIdx = useRef(null);
  return (i) => ({
    draggable: true,
    onDragStart: () => { dragIdx.current = i; },
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => {
      e.preventDefault();
      const from = dragIdx.current;
      if (from === null || from === i) return;
      const next = [...items];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      dragIdx.current = null;
      onReorder(next);
    },
  });
}

/* ---------------- Photo manager (upload, delete, main, reorder, preview) ---------------- */
function PhotoManager({ images, mainImage, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const rowProps = useDragList(images, (next) => onChange({ images: next, main_image: mainImage }));

  const upload = async (files) => {
    setUploading(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try {
      const r = await api.post("/gestion/fleet/upload-photos", fd);
      const urls = r.data.urls || [];
      const next = [...images, ...urls];
      onChange({ images: next, main_image: mainImage || next[0] || "" });
      toast.success(`${urls.length} photo(s) ajoutée(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };
  const remove = (url) => {
    const next = images.filter((u) => u !== url);
    onChange({ images: next, main_image: mainImage === url ? (next[0] || "") : mainImage });
  };

  return (
    <div data-testid="boat-photo-manager">
      <div className="flex flex-wrap gap-3">
        {images.map((url, i) => (
          <div key={url} {...rowProps(i)} className="relative w-24 h-20 border border-slate-200 cursor-move group" data-testid={`boat-photo-${i}`}>
            <img src={fullUrl(url)} alt="" className="w-full h-full object-cover" />
            <button type="button" title="Photo principale" onClick={() => onChange({ images, main_image: url })}
              className={`absolute top-1 left-1 p-0.5 rounded-full ${mainImage === url ? "bg-club-yellow text-navy" : "bg-white/80 text-slate-400 hover:text-navy"}`}
              data-testid={`boat-photo-main-${i}`}>
              <Star className="w-3.5 h-3.5" fill={mainImage === url ? "currentColor" : "none"} />
            </button>
            <button type="button" title={tr("Supprimer")} onClick={() => remove(url)}
              className="absolute top-1 right-1 p-0.5 rounded-full bg-white/80 text-slate-400 hover:text-club-red" data-testid={`boat-photo-del-${i}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="w-24 h-20 border border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-navy hover:text-navy text-[10px] uppercase tracking-widest gap-1"
          data-testid="boat-photo-upload-btn">
          <Upload className="w-4 h-4" /> {uploading ? "…" : "Photos"}
        </button>
        <input type="file" accept="image/*" multiple ref={fileRef} className="hidden"
          onChange={(e) => e.target.files?.length && upload(e.target.files)} data-testid="boat-photo-input" />
      </div>
      {images.length > 0 && <p className="text-[10px] text-slate-400 mt-2">{tr("Glissez-déposez pour réordonner · ★ = photo principale")}</p>}
    </div>
  );
}

/* ---------------- Boat form (add / edit) ---------------- */
const EMPTY_BOAT = { name: "", category_id: "", model: "", description: "", year: "", sail_number: "", internal_code: "", capacity: "", length: "", status: "available", is_active: true, images: [], main_image: "" };

function BoatForm({ boat, cats, onSave, onCancel }) {
  const [f, setF] = useState({ ...EMPTY_BOAT, ...boat, model: boat?.model || boat?.boat_type || "", images: boat?.images || [], main_image: boat?.main_image || "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    const num = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = +v;
      return Number.isFinite(n) ? n : null;
    };
    onSave({
      name: f.name, category_id: f.category_id, model: f.model, description: f.description,
      year: num(f.year), sail_number: f.sail_number, internal_code: f.internal_code,
      capacity: num(f.capacity), length: num(f.length),
      status: f.status, is_active: f.is_active, images: f.images, main_image: f.main_image,
    });
  };
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="fleet-boat-form">
      <div className="md:col-span-3 label-eyebrow text-club-red">{boat?.id ? `${tr("Modifier")} — ${boat.name}` : tr("Ajouter un bateau")}</div>
      <div><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{tr("Nom *")}</label>
        <input required className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="fleet-boat-name" /></div>
      <div><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{tr("Catégorie")}</label>
        <select className={inputCls} value={f.category_id} onChange={(e) => set("category_id", e.target.value)} data-testid="fleet-boat-category">
          <option value="">— Non classé —</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select></div>
      <div><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{tr("Type / modèle")}</label>
        <input className={inputCls} value={f.model} onChange={(e) => set("model", e.target.value)} data-testid="fleet-boat-model" /></div>
      <div><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{tr("Année")}</label>
        <input type="number" className={inputCls} value={f.year ?? ""} onChange={(e) => set("year", e.target.value)} data-testid="fleet-boat-year" /></div>
      <div><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">N° de voile</label>
        <input className={inputCls} value={f.sail_number} onChange={(e) => set("sail_number", e.target.value)} data-testid="fleet-boat-sail" /></div>
      <div><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">Code interne</label>
        <input className={inputCls} value={f.internal_code} onChange={(e) => set("internal_code", e.target.value)} data-testid="fleet-boat-code" /></div>
      <div><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{tr("Capacité (pers.)")}</label>
        <input type="number" className={inputCls} value={f.capacity ?? ""} onChange={(e) => set("capacity", e.target.value)} data-testid="fleet-boat-capacity" /></div>
      <div><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">Longueur (m)</label>
        <input type="number" step="0.01" className={inputCls} value={f.length ?? ""} onChange={(e) => set("length", e.target.value)} data-testid="fleet-boat-length" /></div>
      <div><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{tr("Statut / état")}</label>
        <select className={inputCls} value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="fleet-boat-status">
          {BOAT_STATUSES.map((s) => <option key={s.value} value={s.value}>{tr(s.label)}</option>)}
        </select></div>
      <div className="md:col-span-3"><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-1">{tr("Description")}</label>
        <textarea rows={2} className={`${inputCls} resize-none`} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="fleet-boat-description" /></div>
      <div className="md:col-span-3"><label className="text-[10px] uppercase tracking-widest text-slate-400 block mb-2">Photos</label>
        <PhotoManager images={f.images} mainImage={f.main_image} onChange={({ images, main_image }) => setF((p) => ({ ...p, images, main_image }))} /></div>
      <label className="flex items-center gap-2 text-sm text-navy">
        <input type="checkbox" checked={f.is_active !== false} onChange={(e) => set("is_active", e.target.checked)} data-testid="fleet-boat-active" />
        Visible / actif
      </label>
      <div className="md:col-span-2 flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="text-xs uppercase tracking-widest text-slate-500 hover:text-navy px-4">{tr("Annuler")}</button>
        <button type="submit" className={btnCls} data-testid="fleet-boat-save"><Save className="w-4 h-4" />{tr("Enregistrer")}</button>
      </div>
    </form>
  );
}

/* ---------------- Categories manager ---------------- */
function CategoriesManager({ cats, reload }) {
  const [form, setForm] = useState({ name: "", description: "" });
  const rowProps = useDragList(cats, async (next) => {
    await api.post("/gestion/fleet/categories/reorder", { ids: next.map((c) => c.id) });
    toast.success(tr("Ordre mis à jour"));
    reload();
  });
  const add = async () => {
    try {
      await api.post("/gestion/fleet/categories", form);
      setForm({ name: "", description: "" });
      toast.success(tr("Catégorie créée"));
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const save = async (c) => {
    await api.patch(`/gestion/fleet/categories/${c.id}`, { name: c.name, description: c.description, is_active: c.is_active });
    toast.success(tr("Catégorie enregistrée"));
    reload();
  };
  const remove = async (c) => {
    if (!window.confirm(`${tr("Supprimer la catégorie")} « ${c.name} » ?`)) return;
    try { await api.delete(`/gestion/fleet/categories/${c.id}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const [local, setLocal] = useState(cats);
  useEffect(() => { setLocal(cats); }, [cats]);
  const patchLocal = (id, patch) => setLocal((p) => p.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  return (
    <div data-testid="boat-categories-manager">
      <p className="text-xs text-slate-400 mb-4">{tr("Glissez-déposez pour réordonner. L'œil active/désactive la catégorie.")}</p>
      <div className="space-y-2">
        {local.map((c, i) => (
          <div key={c.id} {...rowProps(i)} className="flex items-center gap-2 bg-white border border-slate-200 p-3 cursor-move flex-wrap" data-testid={`boat-cat-${c.id}`}>
            <GripVertical className="w-4 h-4 text-slate-400 shrink-0" />
            <input className={`${inputCls} flex-1 min-w-[140px]`} value={c.name || ""} onChange={(e) => patchLocal(c.id, { name: e.target.value })} data-testid={`boat-cat-name-${c.id}`} />
            <input className={`${inputCls} flex-[2] min-w-[180px]`} placeholder={tr("Description")} value={c.description || ""} onChange={(e) => patchLocal(c.id, { description: e.target.value })} />
            <button onClick={() => patchLocal(c.id, { is_active: !(c.is_active !== false) })} className={c.is_active !== false ? "text-green-600" : "text-slate-400"} title="Active" data-testid={`boat-cat-toggle-${c.id}`}>
              {c.is_active !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>
            <button onClick={() => save(c)} className="text-navy hover:text-club-red" title={tr("Enregistrer")} data-testid={`boat-cat-save-${c.id}`}><Save className="w-4 h-4" /></button>
            <button onClick={() => remove(c)} className="text-slate-400 hover:text-club-red" title={tr("Supprimer")} data-testid={`boat-cat-del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-4 flex-wrap">
        <input className={`${inputCls} flex-1 min-w-[140px]`} placeholder={tr("Nouvelle catégorie")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="boat-cat-new-name" />
        <input className={`${inputCls} flex-[2] min-w-[180px]`} placeholder="Description (optionnel)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <button onClick={add} className={btnCls} data-testid="boat-cat-add-btn"><Plus className="w-4 h-4" />{tr("Créer")}</button>
      </div>
    </div>
  );
}

/* ---------------- Boat card ---------------- */
function BoatCard({ b, catName, onEdit, onDelete, onToggle, dragProps }) {
  const st = statusOf(b.status || "available");
  const img = b.main_image || (b.images || [])[0];
  return (
    <div {...dragProps} className={`bg-white border border-slate-200 cursor-move ${b.is_active === false ? "opacity-60" : ""}`} data-testid={`fleet-boat-${b.id}`}>
      <div className="aspect-video bg-sand overflow-hidden relative">
        {img ? <img src={fullUrl(img)} alt={b.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-slate-300"><Anchor className="w-8 h-8" strokeWidth={1.25} /></div>}
        <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-0.5 font-semibold ${st.cls}`} data-testid={`fleet-boat-status-${b.id}`}>{tr(st.label)}</span>
        {b.is_active === false && <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-0.5 bg-slate-700 text-white">{tr("Masqué")}</span>}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-serif text-lg text-navy">{b.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-club-yellow-dark mt-0.5">
              {tr(catName || "Non classé")}{(b.model || b.boat_type) ? ` · ${b.model || b.boat_type}` : ""}
            </div>
          </div>
          <GripVertical className="w-4 h-4 text-slate-300 shrink-0 mt-1" />
        </div>
        <div className="text-xs text-slate-500 mt-2">
          {[
            b.sail_number && `Voile ${b.sail_number}`,
            b.internal_code,
            b.year,
            b.capacity && `${b.capacity} pers.`,
            b.length && `${b.length} m`,
          ].filter(Boolean).join(" · ")}
        </div>
        {b.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{b.description}</p>}
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
          <button onClick={onEdit} className="text-[10px] uppercase tracking-widest text-navy hover:underline inline-flex items-center gap-1" data-testid={`fleet-boat-edit-${b.id}`}><Pencil className="w-3 h-3" />{tr("Modifier")}</button>
          <button onClick={onToggle} className="text-[10px] uppercase tracking-widest text-slate-500 hover:underline inline-flex items-center gap-1" data-testid={`fleet-boat-toggle-${b.id}`}>
            {b.is_active === false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} {b.is_active === false ? tr("Activer") : tr("Désactiver")}
          </button>
          <button onClick={onDelete} className="text-[10px] uppercase tracking-widest text-red-600 hover:underline ml-auto" data-testid={`fleet-boat-del-${b.id}`}>{tr("Supprimer")}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Main ---------------- */
export default function FleetManager({ onFleetChanged }) {
  const [sub, setSub] = useState("list");
  const [boats, setBoats] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const onFleetChangedRef = useRef(onFleetChanged);
  onFleetChangedRef.current = onFleetChanged;

  const load = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([api.get("/gestion/fleet/boats"), api.get("/gestion/fleet/categories")]);
      setBoats(b.data || []);
      setCats(c.data || []);
      onFleetChangedRef.current?.();
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const catName = (id) => cats.find((c) => c.id === id)?.name;
  const filtered = filter === "all" ? boats : filter === "none" ? boats.filter((b) => !b.category_id) : boats.filter((b) => b.category_id === filter);

  const groups = [
    ...cats.map((c) => ({ id: c.id, name: c.name, active: c.is_active !== false, boats: filtered.filter((b) => b.category_id === c.id) })),
    { id: "none", name: "Non classé", active: true, boats: filtered.filter((b) => !b.category_id || !cats.find((c) => c.id === b.category_id)) },
  ].filter((g) => g.boats.length > 0);

  const saveBoat = async (payload) => {
    try {
      if (editing?.id) {
        await api.patch(`/gestion/fleet/boats/${editing.id}`, payload);
        toast.success(tr("Bateau mis à jour"));
      } else {
        await api.post("/gestion/fleet/boats", payload);
        toast.success(tr("Bateau créé"));
      }
      setEditing(null);
      setSub("list");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const delBoat = async (b) => {
    if (!window.confirm(`Supprimer « ${b.name} » et ses réservations ?`)) return;
    await api.delete(`/gestion/fleet/boats/${b.id}`);
    load();
  };
  const toggleBoat = async (b) => {
    await api.patch(`/gestion/fleet/boats/${b.id}`, { is_active: !(b.is_active !== false) });
    load();
  };
  const reorderGroup = async (group, nextGroupBoats) => {
    const orderedIds = [];
    groups.forEach((g) => {
      (g.id === group.id ? nextGroupBoats : g.boats).forEach((b) => orderedIds.push(b.id));
    });
    await api.post("/gestion/fleet/boats/reorder", { ids: orderedIds });
    toast.success(tr("Ordre mis à jour"));
    load();
  };

  return (
    <div data-testid="fleet-manager">
      <h3 className="font-serif text-2xl text-navy mb-3">{tr("Flotte du club")} ({boats.length})</h3>
      <div className="divider-gold mb-4" />
      <div className="flex gap-2 flex-wrap mb-6">
        {[["list", "Tous les bateaux"], ["add", "Ajouter un bateau"], ["categories", "Catégories de bateaux"]].map(([k, label]) => (
          <button key={k} onClick={() => { setSub(k); setEditing(null); }}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${sub === k ? "bg-navy text-white border-navy" : "border-slate-300 text-slate-500 hover:border-navy hover:text-navy"}`}
            data-testid={`fleet-sub-${k}`}>
            {tr(label)}
          </button>
        ))}
      </div>

      {sub === "add" && <BoatForm boat={null} cats={cats.filter((c) => c.is_active !== false)} onSave={saveBoat} onCancel={() => setSub("list")} />}
      {sub === "categories" && <CategoriesManager cats={cats} reload={load} />}

      {sub === "list" && (
        <>
          {editing && <BoatForm boat={editing} cats={cats} onSave={saveBoat} onCancel={() => setEditing(null)} />}
          <div className="flex items-center gap-2 mb-6">
            <label className="text-[10px] uppercase tracking-widest text-slate-400 whitespace-nowrap shrink-0">{tr("Filtrer :")}</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${inputCls} w-auto`} data-testid="fleet-filter">
              <option value="all">{tr("Toutes les catégories")}</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              <option value="none">{tr("Non classé")}</option>
            </select>
          </div>
          {groups.length === 0 && <div className="bg-white border border-dashed border-slate-300 p-8 text-center text-slate-500 mb-6">{tr("Aucun bateau.")}</div>}
          {groups.map((g) => (
            <GroupGrid key={g.id} group={g} catName={catName} onEdit={setEditing} onDelete={delBoat} onToggle={toggleBoat} onReorder={reorderGroup} />
          ))}
        </>
      )}
    </div>
  );
}

function GroupGrid({ group, catName, onEdit, onDelete, onToggle, onReorder }) {
  const rowProps = useDragList(group.boats, (next) => onReorder(group, next));
  return (
    <div className="mb-8" data-testid={`fleet-group-${group.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <h4 className="font-serif text-lg text-navy">{tr(group.name)}</h4>
        <span className="text-[10px] uppercase tracking-widest text-slate-400">{group.boats.length} {tr("bateau(x)")}</span>
        {!group.active && <span className="text-[10px] uppercase tracking-widest bg-slate-200 text-slate-500 px-2 py-0.5">{tr("Catégorie désactivée")}</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {group.boats.map((b, i) => (
          <BoatCard key={b.id} b={b} catName={catName(b.category_id)} dragProps={rowProps(i)}
            onEdit={() => onEdit(b)} onDelete={() => onDelete(b)} onToggle={() => onToggle(b)} />
        ))}
      </div>
    </div>
  );
}
